import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Seo from "../../../shared/ui/Seo";
import Modal from "../../../shared/ui/Modal";
import { getWishlist, removeFromWishlist, clearWishlist } from "../api";
import { useCart } from "../../cart/context/CartContext";
import { getPricing } from "../../products/utils/productDisplay";
import { formatPrice } from "../../checkout/utils/pricing";
import { S3_URL_PRODUCTS } from "../../../shared/utils/constants";
import "../styles/wishlist.css";

const WishlistItem = ({ item, onAddToCart, onRemove }) => {
  const { price, original, discountPercentage } = getPricing(item);
  const image = item.images?.[0];
  const link = `/product-details/${item.slug}`;

  return (
    <article className="wl-item">
      <Link to={link} className="wl-item__thumb">
        {image ? (
          <img src={`${S3_URL_PRODUCTS}${image}`} alt={item.name} loading="lazy" />
        ) : (
          <i className="ph ph-image" aria-hidden="true" />
        )}
      </Link>
      <div className="wl-item__info">
        <Link to={link} className="wl-item__name">{item.name}</Link>
        <div className="wl-item__price">
          <span className="wl-item__now">LKR {formatPrice(price)}</span>
          {discountPercentage > 0 && (
            <>
              <span className="wl-item__was">LKR {formatPrice(original)}</span>
              <span className="wl-item__off">-{discountPercentage}%</span>
            </>
          )}
        </div>
        {item.createTime && (
          <span className="wl-item__date">Added {new Date(item.createTime).toLocaleDateString()}</span>
        )}
      </div>
      <div className="wl-item__actions">
        <button type="button" className="ui-btn ui-btn--primary ui-btn--sm" onClick={() => onAddToCart(item.id)}>
          <i className="ph ph-shopping-cart-simple" />
          Add to cart
        </button>
        <button
          type="button"
          className="wl-item__remove"
          onClick={() => onRemove(item.id)}
          aria-label={`Remove ${item.name} from wishlist`}
          title="Remove"
        >
          <i className="ph ph-trash" />
        </button>
      </div>
    </article>
  );
};

const Wishlist = () => {
  const { addToCart } = useCart();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setHasError(false);
    getWishlist()
      .then((response) => {
        if (!ignore) setWishlist(Array.isArray(response) ? response : []);
      })
      .catch((error) => {
        console.error(error);
        if (!ignore) setHasError(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [reloadKey]);

  const handleRemove = async (productId) => {
    try {
      await removeFromWishlist(productId);
      window.dispatchEvent(new Event("wishlistUpdated"));
      setWishlist((items) => items.filter((item) => item.id !== productId));
      toast.success("Item removed from wishlist");
    } catch (error) {
      console.error(error);
      toast.error("Failed to remove item");
    }
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      await clearWishlist();
      setWishlist([]);
      toast.success("Wishlist cleared");
      window.dispatchEvent(new Event("wishlistUpdated"));
      setConfirmClear(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to clear wishlist");
    } finally {
      setClearing(false);
    }
  };

  const handleAddToCart = (id) => {
    try {
      addToCart({ id, quantity: 1 });
    } catch (error) {
      console.error(error);
      toast.error("Failed to add item to cart");
    }
  };

  let content;
  if (loading) {
    content = (
      <div className="wl-list" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="wl-item">
            <span className="wl-item__thumb skeleton-block" />
            <div className="wl-item__info">
              <span className="skeleton-line w-200" />
              <span className="skeleton-line w-120" />
            </div>
          </div>
        ))}
      </div>
    );
  } else if (hasError) {
    content = (
      <div className="ui-empty">
        <i className="ph ph-warning-circle ui-empty__icon" />
        <h2 className="ui-empty__title">We couldn't load your wishlist</h2>
        <p className="ui-empty__text">Please check your connection and try again.</p>
        <button type="button" className="ui-btn ui-btn--primary ui-btn--sm" onClick={() => setReloadKey((k) => k + 1)}>
          Try again
        </button>
      </div>
    );
  } else if (wishlist.length === 0) {
    content = (
      <div className="ui-empty">
        <i className="ph ph-heart ui-empty__icon" />
        <h2 className="ui-empty__title">Your wishlist is empty</h2>
        <p className="ui-empty__text">Tap the heart on any product to save it here.</p>
        <Link to="/products" className="ui-btn ui-btn--primary ui-btn--sm">
          Start shopping
        </Link>
      </div>
    );
  } else {
    content = (
      <div className="wl-list">
        {wishlist.map((item) => (
          <WishlistItem key={item.id} item={item} onAddToCart={handleAddToCart} onRemove={handleRemove} />
        ))}
      </div>
    );
  }

  return (
    <section className="wl-page py-20">
      <Seo title="Wishlist" noindex />
      <div className="wl-head">
        <div className="ui-head">
          <h1 className="ui-head__title">
            Wishlist {!loading && wishlist.length > 0 && <span className="wl-head__count">{wishlist.length}</span>}
          </h1>
          <p className="ui-head__text">Products you saved for later.</p>
        </div>
        {!loading && wishlist.length > 0 && (
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm" onClick={() => setConfirmClear(true)}>
            <i className="ph ph-trash-simple" />
            Clear all
          </button>
        )}
      </div>

      {content}

      <Modal
        isOpen={confirmClear}
        onClose={() => setConfirmClear(false)}
        title="Clear your wishlist?"
        subtitle="All saved products will be removed. This can't be undone."
        size="sm"
        dismissible={!clearing}
        footer={
          <>
            <button type="button" className="ui-btn ui-btn--ghost" onClick={() => setConfirmClear(false)} disabled={clearing}>
              Cancel
            </button>
            <button type="button" className="ui-btn ui-btn--danger" onClick={handleClear} disabled={clearing}>
              {clearing ? "Clearing…" : "Clear wishlist"}
            </button>
          </>
        }
      />
    </section>
  );
};

export default Wishlist;
